import { ArrowRight, Globe, Search, Target, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const DigitalMarketing = () => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-yellow-50/30">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Digital Marketing Services
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Digital marketing can help you reach your target audience more effectively and efficiently.
          </p>
        </div>
      </section>

      {/* Description */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#FBD116]/10 border-l-4 border-[#FBD116] p-8 rounded-r-2xl">
            <p className="text-lg text-gray-900">
              Digital marketing is the process of creating, managing, and executing a marketing plan that uses digital technologies to reach and engage customers. It uses a mix of digital channels including websites, search engines, social media, email, and mobile apps.
            </p>
          </div>
        </div>
      </section>

      {/* Web Design & Development */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Web Design & Development
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Why do you need a good website? Awareness, sales, and information. Don't rely on your Facebook page. You will never own it, and that comes with risk. Having a well-designed website will give you credibility and make you look professional.
                </p>
                <p className="font-semibold text-gray-900">The following statistics show how important a website is for a local business:</p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#FBD116] mr-3 flex-shrink-0 mt-1" />
                    <span>46% of search on Google include local intent</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#FBD116] mr-3 flex-shrink-0 mt-1" />
                    <span>97% of users use search to find local businesses</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#FBD116] mr-3 flex-shrink-0 mt-1" />
                    <span>28% of users purchased an item after a local search</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#FBD116] mr-3 flex-shrink-0 mt-1" />
                    <span>70% of users will visit a store because of what they found online</span>
                  </li>
                </ul>
                <p className="text-gray-900 font-medium">It's not as complicated as you think.</p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=800&h=600&fit=crop"
                alt="Web design"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* SEO & Targeted Ads */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop"
                alt="SEO Analytics"
                className="rounded-2xl shadow-2xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                SEO & Targeted Ads
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  SEO (Search Engine Optimization) is the practice of growing a website's traffic from organic search results. It involves things like keyword research, content creation, link building, and technical audits.
                </p>
                <p>
                  Targeted advertising allows you to reach specific audiences based on demographics, interests, and behaviors. Combined with SEO, it creates a powerful digital marketing strategy.
                </p>
                <p className="text-gray-900 font-medium">
                  You can get started from 5 hours per month + Setup.
                </p>
              </div>
              <div className="mt-8">
                <Button
                  asChild
                  className="bg-[#FBD116] hover:bg-[#E5C015] text-gray-900 font-semibold"
                >
                  <a href="https://calendly.com/bronny-mpremarketing/30min" target="_blank" rel="noopener noreferrer">
                    Get Started
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Cards */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Our Digital Marketing Services
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Globe,
                title: 'Website Design & Development',
                description: 'Professional, conversion-focused websites that represent your brand and drive results.'
              },
              {
                icon: Search,
                title: 'SEO Optimization',
                description: 'Improve your search rankings and drive organic traffic to your website.'
              },
              {
                icon: Target,
                title: 'Targeted Advertising',
                description: 'Reach the right audience at the right time with strategic paid campaigns.'
              }
            ].map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border-2 hover:border-[#FBD116] transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-[#FBD116]/10 flex items-center justify-center mx-auto mb-6">
                      <Icon className="w-8 h-8 text-gray-900" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom Image */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1432888622747-4eb9a8f2c293?w=1200&h=675&fit=crop"
              alt="Digital Marketing Success"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">
            Need Help?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Book a no-obligation chat to discuss your digital marketing needs.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-[#FBD116] hover:bg-[#E5C015] text-gray-900 font-semibold text-lg px-8"
          >
            <a href="https://calendly.com/bronny-mpremarketing/30min" target="_blank" rel="noopener noreferrer">
              Get Help
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default DigitalMarketing;
